// ============================================
// Launch Gate Readiness Checker
//
// Determines whether a brand's test suite passes
// the readiness threshold before going live.
// Evaluates overall pass rate, per-archetype
// results, critical failures, and kill-switch checks.
// ============================================

import type { TestRun, CheckResult, SeverityLevel } from '../../types/patientJourney';
import type { PJEConfig } from './pjeConfig';
import { getPJEConfig } from './pjeConfig';

// --- Thresholds ---

const DEFAULT_OVERALL_THRESHOLD = 85;
const DEFAULT_ARCHETYPE_THRESHOLD = 75;

// --- Result Interfaces ---

export interface LaunchGateResult {
  ready: boolean;
  overallPassRate: number;
  threshold: number;
  archetypeResults: ArchetypeGateResult[];
  blockingIssues: string[];
  warnings: string[];
  assessedAt: string;
  suiteId: string;
  brandId: string;
}

export interface ArchetypeGateResult {
  archetypeId: string;
  archetypeName: string;
  passRate: number;
  passed: boolean;
  criticalFailures: string[];
  runCount: number;
}

// --- Severity Mapping ---

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  major: 1,
  minor: 2,
};

/**
 * Maps clinical SeverityLevel to check-item severity for cross-referencing
 * between the PJE severity system and the per-check severity grades.
 */
export function mapClinicalSeverity(level: SeverityLevel): string {
  const mapping: Record<SeverityLevel, string> = {
    critical: 'critical',
    urgent: 'critical',
    attention: 'major',
    routine: 'minor',
  };
  return mapping[level];
}

// --- Helpers ---

function extractArchetypeId(personaId: string): string {
  const underscoreIndex = personaId.indexOf('_');
  return underscoreIndex === -1 ? personaId : personaId.substring(0, underscoreIndex);
}

function formatArchetypeName(archetypeId: string): string {
  return archetypeId
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function inferCheckSeverity(checkId: string): string {
  // Sentiment kill-switch and attribution checks are treated as critical
  if (checkId.startsWith('V3-SEN')) return 'critical';
  if (checkId.startsWith('V3-ATT')) return 'critical';
  // Clinical checks with critical prefix patterns
  if (checkId.includes('_fc_') || checkId.includes('_co_')) return 'major';
  return 'major';
}

function computePassRate(checkResults: CheckResult[]): number {
  const scoredChecks = checkResults.filter((cr) => cr.pass !== null);
  if (scoredChecks.length === 0) return 0;
  const passed = scoredChecks.filter((cr) => cr.pass === true).length;
  return (passed / scoredChecks.length) * 100;
}

// --- Core Assessment ---

export function assessLaunchReadiness(
  runs: TestRun[],
  suiteId: string,
  brandId: string,
  config?: PJEConfig,
): LaunchGateResult {
  const resolvedConfig = config ?? getPJEConfig(brandId);
  const threshold = DEFAULT_OVERALL_THRESHOLD;
  const archetypeThreshold = DEFAULT_ARCHETYPE_THRESHOLD;
  const sentimentKillSwitchPct = resolvedConfig.sentimentKillSwitchPct;

  const blockingIssues: string[] = [];
  const warnings: string[] = [];

  // --- Collect all check results across runs ---

  const allCheckResults: CheckResult[] = [];
  for (const run of runs) {
    allCheckResults.push(...run.checkResults);
  }

  // --- Overall pass rate ---

  const overallPassRate = computePassRate(allCheckResults);

  // --- Group runs by archetype ---

  const archetypeMap = new Map<string, TestRun[]>();
  for (const run of runs) {
    const archetypeId = extractArchetypeId(run.personaId);
    const group = archetypeMap.get(archetypeId);
    if (group) {
      group.push(run);
    } else {
      archetypeMap.set(archetypeId, [run]);
    }
  }

  const archetypeResults: ArchetypeGateResult[] = [];

  for (const [archetypeId, archetypeRuns] of archetypeMap) {
    const archetypeChecks: CheckResult[] = [];
    const criticalFailures: string[] = [];

    for (const run of archetypeRuns) {
      for (const cr of run.checkResults) {
        archetypeChecks.push(cr);
        if (cr.pass === false) {
          const severity = inferCheckSeverity(cr.checkId);
          if (severity === 'critical') {
            criticalFailures.push(cr.checkId);
          }
        }
      }
    }

    const passRate = computePassRate(archetypeChecks);
    const passed = passRate >= archetypeThreshold;

    archetypeResults.push({
      archetypeId,
      archetypeName: formatArchetypeName(archetypeId),
      passRate: Math.round(passRate * 100) / 100,
      passed,
      criticalFailures: [...new Set(criticalFailures)],
      runCount: archetypeRuns.length,
    });

    if (!passed) {
      blockingIssues.push(
        `Archetype "${formatArchetypeName(archetypeId)}" pass rate ${passRate.toFixed(1)}% is below the ${archetypeThreshold}% threshold`,
      );
    }
  }

  // --- Critical severity failures are blocking ---

  for (const cr of allCheckResults) {
    if (cr.pass === false) {
      const severity = inferCheckSeverity(cr.checkId);
      if (severity === 'critical') {
        const issue = `Critical check failed: ${cr.checkId}`;
        if (!blockingIssues.includes(issue)) {
          blockingIssues.push(issue);
        }
      }
    }
  }

  // --- Sentiment kill-switch checks (V3-SEN) must ALL pass ---

  const sentimentChecks = allCheckResults.filter((cr) => cr.checkId.startsWith('V3-SEN'));
  const failedSentimentChecks = sentimentChecks.filter((cr) => cr.pass === false);
  if (failedSentimentChecks.length > 0) {
    const uniqueIds = [...new Set(failedSentimentChecks.map((cr) => cr.checkId))];
    for (const id of uniqueIds) {
      const issue = `Sentiment kill-switch check failed: ${id} (kill-switch threshold: ${sentimentKillSwitchPct}%)`;
      if (!blockingIssues.includes(issue)) {
        blockingIssues.push(issue);
      }
    }
  }

  // --- Attribution checks (V3-ATT) must ALL pass ---

  const attributionChecks = allCheckResults.filter((cr) => cr.checkId.startsWith('V3-ATT'));
  const failedAttributionChecks = attributionChecks.filter((cr) => cr.pass === false);
  if (failedAttributionChecks.length > 0) {
    const uniqueIds = [...new Set(failedAttributionChecks.map((cr) => cr.checkId))];
    for (const id of uniqueIds) {
      const issue = `Attribution check failed: ${id}`;
      if (!blockingIssues.includes(issue)) {
        blockingIssues.push(issue);
      }
    }
  }

  // --- Overall threshold ---

  if (overallPassRate < threshold) {
    blockingIssues.push(
      `Overall pass rate ${overallPassRate.toFixed(1)}% is below the ${threshold}% threshold`,
    );
  }

  // --- Warnings for non-blocking issues ---

  const unscoredChecks = allCheckResults.filter((cr) => cr.pass === null);
  if (unscoredChecks.length > 0) {
    warnings.push(`${unscoredChecks.length} check(s) have not been scored yet`);
  }

  for (const result of archetypeResults) {
    if (result.passed && result.passRate < threshold) {
      warnings.push(
        `Archetype "${result.archetypeName}" passes at ${result.passRate.toFixed(1)}% but is below the overall ${threshold}% target`,
      );
    }
  }

  const failedRuns = runs.filter((r) => r.status === 'failed' || r.status === 'timed_out');
  if (failedRuns.length > 0) {
    warnings.push(`${failedRuns.length} run(s) failed or timed out and were excluded from scoring`);
  }

  // --- Final readiness determination ---

  const allArchetypesPassed = archetypeResults.every((ar) => ar.passed);
  const noSentimentFailures = failedSentimentChecks.length === 0;
  const noAttributionFailures = failedAttributionChecks.length === 0;
  const noCriticalFailures = allCheckResults.every(
    (cr) => cr.pass !== false || inferCheckSeverity(cr.checkId) !== 'critical',
  );
  const meetsOverallThreshold = overallPassRate >= threshold;

  const ready =
    meetsOverallThreshold &&
    allArchetypesPassed &&
    noCriticalFailures &&
    noSentimentFailures &&
    noAttributionFailures;

  return {
    ready,
    overallPassRate: Math.round(overallPassRate * 100) / 100,
    threshold,
    archetypeResults,
    blockingIssues,
    warnings,
    assessedAt: new Date().toISOString(),
    suiteId,
    brandId,
  };
}

// --- Blocking Checks Report ---

export function getBlockingChecks(
  runs: TestRun[],
): { checkId: string; check: string; failCount: number; severity: string }[] {
  const failMap = new Map<string, { checkId: string; check: string; failCount: number; severity: string }>();

  for (const run of runs) {
    for (const cr of run.checkResults) {
      if (cr.pass === false) {
        const existing = failMap.get(cr.checkId);
        if (existing) {
          existing.failCount++;
        } else {
          failMap.set(cr.checkId, {
            checkId: cr.checkId,
            check: cr.notes ?? cr.checkId,
            failCount: 1,
            severity: inferCheckSeverity(cr.checkId),
          });
        }
      }
    }
  }

  const results = Array.from(failMap.values());

  results.sort((a, b) => {
    const orderA = SEVERITY_ORDER[a.severity] ?? 99;
    const orderB = SEVERITY_ORDER[b.severity] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return b.failCount - a.failCount;
  });

  return results;
}

// --- Human-Readable Report ---

export function formatReadinessReport(result: LaunchGateResult): string {
  const lines: string[] = [];

  lines.push('========================================');
  lines.push('  LAUNCH GATE READINESS REPORT');
  lines.push('========================================');
  lines.push('');
  lines.push(`Brand:    ${result.brandId}`);
  lines.push(`Suite:    ${result.suiteId}`);
  lines.push(`Assessed: ${result.assessedAt}`);
  lines.push('');

  // --- Overall verdict ---

  const verdict = result.ready ? 'PASS' : 'FAIL';
  lines.push(`Overall Verdict: ${verdict}`);
  lines.push(`Overall Pass Rate: ${result.overallPassRate.toFixed(1)}% (threshold: ${result.threshold}%)`);
  lines.push('');

  // --- Archetype breakdown ---

  lines.push('----------------------------------------');
  lines.push('  Archetype Breakdown');
  lines.push('----------------------------------------');

  for (const ar of result.archetypeResults) {
    const status = ar.passed ? '[PASS]' : '[FAIL]';
    lines.push(`  ${status} ${ar.archetypeName}`);
    lines.push(`         Pass Rate: ${ar.passRate.toFixed(1)}% | Runs: ${ar.runCount}`);
    if (ar.criticalFailures.length > 0) {
      lines.push(`         Critical Failures: ${ar.criticalFailures.join(', ')}`);
    }
  }

  lines.push('');

  // --- Blocking issues ---

  if (result.blockingIssues.length > 0) {
    lines.push('----------------------------------------');
    lines.push('  Blocking Issues');
    lines.push('----------------------------------------');
    for (const issue of result.blockingIssues) {
      lines.push(`  * ${issue}`);
    }
    lines.push('');
  }

  // --- Warnings ---

  if (result.warnings.length > 0) {
    lines.push('----------------------------------------');
    lines.push('  Warnings');
    lines.push('----------------------------------------');
    for (const warning of result.warnings) {
      lines.push(`  - ${warning}`);
    }
    lines.push('');
  }

  lines.push('========================================');

  return lines.join('\n');
}
